/* state/prospekt-data.js — lazy-fetchade datakällor för områdesrapporten.
 *
 * Områdesrapporten (ui/omrade-rapport.js) svarar på "vad gäller i mitt ritade
 * område?". Den läser INTE kartan — den hämtar datafilerna själv.
 *
 * Varför inte queryRenderedFeatures/querySourceFeatures?
 *   Båda är viewport- och zoom-bundna: de returnerar bara det som råkar vara
 *   renderat just nu, i de tiles som råkar vara laddade. En rapport som ändrar
 *   svar när användaren panorerar eller zoomar är värdelös — och värre, den
 *   skulle tyst utelämna träffar utanför vyn. Ett tillstånd du missar för att
 *   du var utzoomad är precis det fel rapporten finns för att förhindra.
 *   Rapporten ska heller inte kräva att rätt lager är påslaget.
 *
 * Konsekvens: allt som ska in i rapporten måste vara klientfetchbar GeoJSON.
 * Det gäller alla lager här. Vector-pmtiles (jordart, berggrund) kan INTE
 * läsas så — de kan bara nås via renderade tiles. Se ui/omrade-rapport.js
 * för hur det hanteras (och varför berggrund saknas i rapporten).
 *
 * Fail-open: en källa som 404:ar (t.ex. data/_generated/* som är gitignorerad
 * och bara finns i VPS:ens shared-mapp, /CLAUDE.md §5 + fälla #25) fäller inte
 * rapporten — den markeras som oläsbar så användaren ser att sektionen är
 * ofullständig istället för att tro att den är tom.
 *
 * Public API:
 *   loadKallor(nycklar)  → Promise<Map<nyckel, {fc|null, fel|null, meta}>>
 *   KALLOR               → källtabellen (nyckel → {url, titel, ...})
 */

// url:  absolut path (extensionless-konventionen kräver /data/… — se §13)
// titel: rubrik i rapporten
// attribution: visas när källan bidragit med träffar (CC-BY kräver det;
//              SGU:s CC0 gör det inte, men vi visar ändå)
export const KALLOR = {
    ratt: {
        url: '/data/mineralrattigheter.geojson?v=1',
        titel: 'Mineralrättigheter',
        titelKey: 'karta_kalla_ratt',
        attribution: '© SGU / Bergsstaten',
    },
    ratt_hist: {
        url: '/data/mineralrattigheter_historiska.geojson?v=1',
        titel: 'Historiska undersökningstillstånd',
        titelKey: 'karta_kalla_ratt_hist',
        attribution: '© SGU / Bergsstaten',
    },
    gruvor: {
        url: '/data/gruvor.geojson?v=1',
        titel: 'Gruvor',
        titelKey: 'karta_kalla_gruvor',
        attribution: '© SGU',
    },
    resurser: {
        url: '/data/mineralresurser.geojson?v=1',
        titel: 'Mineralförekomster',
        titelKey: 'karta_kalla_resurser',
        attribution: '© SGU',
    },
    takter: {
        url: '/data/takter.geojson?v=1',
        titel: 'Täkter',
        titelKey: 'karta_kalla_takter',
        attribution: '© OpenStreetMap (ODbL)',
    },
    ren_omrade: {
        url: '/data/rennaring_omrade.geojson?v=1',
        titel: 'Renskötselområdet',
        titelKey: 'karta_kalla_ren_omrade',
        attribution: '© Sametinget (CC0)',
    },
    ren_riks: {
        url: '/data/rennaring_riksintresse.geojson?v=1',
        titel: 'Riksintresse rennäring',
        titelKey: 'karta_kalla_ren_riks',
        attribution: 'Sametinget / Länsstyrelserna (CC BY 4.0)',
    },
    naturreservat: {
        url: '/data/_generated/naturreservat.min.geojson?v=3',
        titel: 'Naturreservat',
        titelKey: 'karta_kalla_naturreservat',
        attribution: '© Naturvårdsverket',
    },
    natura2000: {
        url: '/data/_generated/natura2000.min.geojson?v=1',
        titel: 'Natura 2000',
        titelKey: 'karta_kalla_natura2000',
        attribution: '© Naturvårdsverket',
    },
    ri_mineral: {
        url: '/data/_generated/bestammelser_ri_mineral.min.geojson?v=1',
        titel: 'Riksintresse värdefulla ämnen',
        titelKey: 'karta_kalla_ri_mineral',
        attribution: '© SGU',
    },
    ri_naturvard: {
        url: '/data/_generated/bestammelser_ri_naturvard.min.geojson?v=1',
        titel: 'Riksintresse naturvård',
        titelKey: 'karta_kalla_ri_naturvard',
        attribution: '© Länsstyrelserna',
    },
    ri_kultur: {
        url: '/data/_generated/bestammelser_ri_kultur.min.geojson?v=1',
        titel: 'Riksintresse kulturmiljövård',
        titelKey: 'karta_kalla_ri_kultur',
        attribution: '© Länsstyrelserna',
    },
    kommuner: {
        // ?v= KRÄVS på *.min.geojson — de servas `immutable` i ett år
        // (bunny.md §Cache-policy). Vanlig .geojson får 3600 och klarar sig. §6.
        url: '/data/osm/kommuner_scb.min.geojson?v=scb2',
        titel: 'Kommuner',
        titelKey: 'karta_kalla_kommuner',
        attribution: '© SCB',
    },

    /* ── Närhetsanalysen (util/geo-distance.js) ───────────────────────────
     * Till skillnad från ovanstående används de här inte för "berör mitt
     * område?" utan för "hur långt bort?". Samma fail-open gäller: en källa
     * som inte går att läsa ska SÄGA det, inte tolkas som "inget i närheten".
     * anrikningsverk saknas ännu som datafil — posten finns för att felet ska
     * bli synligt ("kunde inte läsa") istället för tyst utelämnat. */
    vagnat: {
        url: '/data/vagnat_stora.geojson?v=2',
        titel: 'Större vägar',
        titelKey: 'karta_kalla_vagnat',
        attribution: '© OpenStreetMap (ODbL)',
    },
    jarnvag: {
        url: '/data/jarnvag_linjer.geojson?v=1',
        titel: 'Järnväg',
        titelKey: 'karta_kalla_jarnvag',
        attribution: '© OpenStreetMap (ODbL)',
    },
    kraftledning: {
        url: '/data/kraftledning_linjer.geojson?v=2',
        titel: 'Kraftledningar',
        titelKey: 'karta_kalla_kraftledning',
        attribution: '© OpenStreetMap (ODbL)',
    },
    tatorter: {
        url: '/data/tatorter.geojson?v=1',
        titel: 'Tätorter',
        titelKey: 'karta_kalla_tatorter',
        attribution: '© SCB',
    },
    anrikningsverk: {
        url: '/data/anrikningsverk.geojson?v=1',
        titel: 'Anrikningsverk',
        titelKey: 'karta_kalla_anrikningsverk',
        attribution: '© SGU',
    },
};

const cache = new Map();      // nyckel → Promise<{fc, fel, meta}>

function fetchKalla(nyckel) {
    const def = KALLOR[nyckel];
    if (!def) return Promise.resolve({ fc: null, fel: 'okänd källa', meta: null });
    return fetch(def.url)
        .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.json();
        })
        .then((fc) => ({
            fc,
            fel: null,
            meta: {
                // attribution/licens kan ligga i filen (build_rennaring.py sätter
                // dem) — filens egen uppgift vinner över källtabellens.
                attribution: fc.attribution || def.attribution || '',
                licens: fc.licens || '',
                farEjSaljas: !!fc.far_ej_saljas,
            },
        }))
        .catch((e) => ({ fc: null, fel: String(e.message || e), meta: null }));
}

/**
 * Hämtar (och cachar) flera källor parallellt.
 * Returnerar alltid en post per begärd nyckel — trasiga källor får fel-sträng
 * istället för att fälla hela anropet.
 */
/* Källans rubrik på användarens språk.
 *
 * Syns bara i felvägen ("Kunde inte läsa Gruvor"), i både modalen och PDF:en —
 * men just därför är den lätt att glömma, och en svensk källrubrik mitt i en
 * engelsk felmening ser ut som en bugg i datat i stället för i i18n:en.
 *
 * ⚠ Resolveras vid ANROP: KALLOR byggs när modulen parsas, före locale-JSON:en
 * landat. Samma skäl som lagerTitel() i util/map-export.js.
 */
export function kallaTitel(nyckel) {
    const k = KALLOR[nyckel];
    if (!k) return nyckel;
    const i = (typeof window !== 'undefined') ? window.i18n : null;
    if (k.titelKey && i && i.isReady && i.isReady()) {
        const r = i.t(k.titelKey);
        if (r !== k.titelKey) return r;
    }
    return k.titel || nyckel;
}

export function loadKallor(nycklar) {
    const jobb = nycklar.map((n) => {
        if (!cache.has(n)) cache.set(n, fetchKalla(n));
        return cache.get(n).then((res) => [n, res]);
    });
    return Promise.all(jobb).then((par) => new Map(par));
}
